import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, BACKEND_URL, downloadExport } from "../lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/Shared";
import Money from "../components/Money";
import { fmtFCFA, fmtDate, statusBadgeClass, ROLES } from "../lib/format";
import { isValidEmail } from "../lib/validate";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, TrendingUp, Receipt, CreditCard, Scale, Link2, RefreshCw, Plus, Trash2, GripVertical, CheckCircle2, Circle, Clock, LayoutTemplate, AlertTriangle, Sun, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import ExportMenu from "../components/ExportMenu";
import { toast } from "sonner";

const MS_STATUS = {
  pending: { label: "Pending", icon: Circle, dot: "bg-slate-300", text: "text-slate-500" },
  in_progress: { label: "In Progress", icon: Clock, dot: "bg-blue-500", text: "text-blue-600" },
  done: { label: "Done", icon: CheckCircle2, dot: "bg-emerald-500", text: "text-emerald-600" },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const { isRole, user } = useAuth();
  const [portal, setPortal] = useState({ enabled: true, token: "", expires: "" });
  const [milestones, setMilestones] = useState([]);
  const [savingMs, setSavingMs] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [msEmails, setMsEmails] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const canManageMs = isRole(ROLES.CEO, ROLES.GM);
  const canSolar = isRole(ROLES.CEO, ROLES.GM, ROLES.ACEO, ROLES.FM);

  const qc = useQueryClient();
  const { data: p = null } = useQuery({ queryKey: ["project", id], queryFn: () => api.get(`/projects/${id}`).then((r) => r.data) });
  const { data: teamMembers = [] } = useQuery({ queryKey: ["project-team", id], queryFn: () => api.get(`/projects/${id}/team`).then((r) => r.data) });
  const { data: allEmployees = [] } = useQuery({ queryKey: ["employees-lite"], queryFn: () => api.get("/employees").then((r) => r.data), enabled: canManageMs });
  const { data: solarAll = [] } = useQuery({ queryKey: ["solar-sizings"], queryFn: () => api.get("/solar-sizings").then((r) => r.data), enabled: canSolar });
  const projectSizings = solarAll.filter((s) => s.project_id === id);
  const [team, setTeam] = useState([]);
  const [teamRoles, setTeamRoles] = useState({});
  const [savingTeam, setSavingTeam] = useState(false);
  const toggleTeam = (eid) => setTeam((t) => (t.includes(eid) ? t.filter((x) => x !== eid) : [...t, eid]));
  const setRole = (eid, val) => setTeamRoles((r) => ({ ...r, [eid]: val }));
  const saveTeam = async () => {
    setSavingTeam(true);
    try { await api.patch(`/projects/${p.id}/team`, { employee_ids: team, roles: teamRoles }); toast.success("Team updated"); qc.invalidateQueries({ queryKey: ["project", id] }); qc.invalidateQueries({ queryKey: ["project-team", id] }); }
    catch { toast.error("Could not update team"); }
    finally { setSavingTeam(false); }
  };
  const { data: templates = [] } = useQuery({ queryKey: ["milestone-templates"], queryFn: () => api.get("/milestone-templates").then((r) => r.data), enabled: canManageMs });
  useEffect(() => {
    if (p) {
      setPortal({ enabled: p.portal_enabled !== false, token: p.public_token, expires: p.portal_expires_at || "" });
      setMilestones([...(p.milestones || [])].sort((a, b) => (a.order || 0) - (b.order || 0)));
      setMsEmails(p.milestone_emails_enabled !== false);
      setTestEmail(p.client_email || "");
      setTeam(p.assigned_employees || []);
      setTeamRoles(p.team_roles || {});
    }
  }, [p]);

  const portalUrl = `${window.location.origin}/portal/${portal.token}`;
  const portalExpired = portal.expires ? new Date(portal.expires.length <= 10 ? `${portal.expires}T23:59:59` : portal.expires) < new Date() : false;
  const togglePortal = async (val) => {
    try { await api.patch(`/projects/${p.id}/portal`, { enabled: val }); setPortal((s) => ({ ...s, enabled: val })); toast.success(val ? "Portal enabled" : "Portal disabled"); }
    catch { toast.error("Update failed"); }
  };
  const regenPortal = async () => {
    try { const { data } = await api.post(`/projects/${p.id}/portal/regenerate`); setPortal((s) => ({ ...s, enabled: true, token: data.public_token })); toast.success("New portal link generated"); }
    catch { toast.error("Failed to regenerate"); }
  };
  const setPortalExpiry = async (val) => {
    try { const { data } = await api.patch(`/projects/${p.id}/portal`, { expires_at: val || null }); setPortal((s) => ({ ...s, expires: data.expires_at || "" })); toast.success(val ? "Expiry updated" : "Expiry cleared"); }
    catch { toast.error("Update failed"); }
  };
  const presetExpiry = (days) => { const d = new Date(); d.setDate(d.getDate() + days); setPortalExpiry(d.toISOString().slice(0, 10)); };

  const addMs = () => setMilestones((m) => [...m, { id: `new-${Date.now()}`, title: "", status: "pending", date: "" }]);
  const updateMs = (idx, patch) => setMilestones((m) => m.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const removeMs = (idx) => setMilestones((m) => m.filter((_, i) => i !== idx));
  const dropMs = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); return; }
    setMilestones((m) => { const a = [...m]; const [it] = a.splice(dragIdx, 1); a.splice(i, 0, it); return a; });
    setDragIdx(null);
  };
  const applyTemplate = (tpl) => {
    setMilestones(tpl.stages.map((title, i) => ({ id: `t-${Date.now()}-${i}`, title, status: "pending", date: "" })));
    toast.success(`Applied "${tpl.name}" — click Save timeline to keep it`);
  };
  const saveTemplate = async () => {
    const stages = milestones.map((m) => (m.title || "").trim()).filter(Boolean);
    if (!tplName.trim() || stages.length === 0) { toast.error("Add a template name and at least one stage"); return; }
    try {
      await api.post("/milestone-templates", { name: tplName.trim(), stages });
      toast.success("Template saved");
      setTplOpen(false); setTplName("");
      qc.invalidateQueries({ queryKey: ["milestone-templates"] });
    } catch { toast.error("Failed to save template"); }
  };
  const toggleMsEmails = async (val) => {
    try { await api.patch(`/projects/${p.id}/milestone-emails`, { enabled: val }); setMsEmails(val); toast.success(val ? "Client reminders on" : "Client reminders off"); }
    catch { toast.error("Update failed"); }
  };
  const sendTestReminder = async () => {
    if (testEmail && !isValidEmail(testEmail)) { toast.error("Enter a valid email address."); return; }
    try { const { data } = await api.post(`/projects/${p.id}/milestone-emails/test`, { email: testEmail || undefined }); toast.success(`Test preview sent to ${data.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send test email"); }
  };
  const saveMs = async () => {
    const payload = milestones.filter((m) => (m.title || "").trim()).map((m) => ({ title: m.title.trim(), status: m.status, date: m.date || null }));
    setSavingMs(true);
    try {
      const { data } = await api.put(`/projects/${p.id}/milestones`, { milestones: payload });
      setMilestones([...data.milestones].sort((a, b) => (a.order || 0) - (b.order || 0)));
      toast.success("Timeline saved");
    } catch { toast.error("Failed to save timeline"); }
    finally { setSavingMs(false); }
  };

  if (!p) return <div className="text-slate-400">Loading project…</div>;
  const fin = p.financials || {};
  const payPct = fin.total_cost > 0 ? Math.round(((fin.amount_received || 0) / fin.total_cost) * 100) : 0;

  return (
    <div>
      <Button asChild variant="ghost" className="mb-2 -ml-2 text-slate-500" data-testid="back-projects">
        <Link to="/projects"><ArrowLeft className="h-4 w-4 mr-1" /> Back to Projects</Link>
      </Button>
      <PageHeader title={p.name} subtitle={`${p.category} · ${p.location || "—"}`}>
        <ExportMenu filename="stems_project" pdfPath={`/export/project/${p.id}/pdf`} pdfLabel="Project PDF" testId="project-detail-export" />
        <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${statusBadgeClass(p.status)}`}>{p.status}</span>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-heading font-semibold text-slate-800 mb-4">Project Overview</h3>
          <div className="grid grid-cols-2 gap-y-4 text-sm">
            <Info label="Owner / Client" value={p.owner} />
            <Info label="Contract Value" value={fmtFCFA(p.total_cost)} mono />
            <Info label="Start Date" value={fmtDate(p.start_date)} />
            <Info label="Expected Completion" value={fmtDate(p.end_date)} />
            <Info label="Created By" value={p.created_by_name} />
            <Info label="Created" value={fmtDate(p.created_at)} />
          </div>
          {(p.activities?.length > 0 || p.description) && (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Activities</div>
              {p.activities?.length > 0 ? (
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600" data-testid="detail-activities">
                  {p.activities.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              ) : (
                <p className="text-sm text-slate-600">{p.description}</p>
              )}
            </div>
          )}
          {p.notes && <p className="mt-2 text-sm text-slate-500">Remarks: {p.notes}</p>}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-heading font-semibold text-slate-800 mb-4">Financial Summary</h3>
          <div className="space-y-3">
            <FinRow icon={TrendingUp} label="Total Income" value={fmtFCFA(fin.total_income)} color="text-emerald-700" />
            <FinRow icon={Receipt} label="Total Expenditure" value={fmtFCFA(fin.total_expenditure)} color="text-slate-800" />
            <FinRow icon={CreditCard} label="Total Payments" value={fmtFCFA(fin.total_payments)} color="text-slate-800" />
            <div className="border-t border-slate-100 pt-3 flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Scale className="h-4 w-4" /> Balance</span>
              <Money value={fin.balance} />
            </div>
            <div className="border-t border-slate-100 pt-3 flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Payment received</span>
              <span className="font-mono-num font-semibold text-emerald-700" data-testid="detail-amount-received">{fmtFCFA(fin.amount_received)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Contract balance</span>
              {fin.payment_complete
                ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="detail-payment-complete"><CheckCircle2 className="h-3.5 w-3.5" /> Payment complete</span>
                : <span className="font-mono-num font-medium" data-testid="detail-contract-balance">{fmtFCFA(fin.contract_balance)}</span>}
            </div>
            <div className="pt-1">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span>Payment progress</span>
                <span className="font-medium text-slate-600" data-testid="detail-payment-pct">{payPct}% of {fmtFCFA(fin.total_cost)}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full ${fin.over_paid ? "bg-red-500" : fin.payment_complete ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${Math.min(100, payPct)}%` }} />
              </div>
              {fin.over_paid && (
                <div className="mt-2 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="detail-overpaid-warning">⚠ Payments received exceed the contract value by {fmtFCFA(fin.overpaid_amount)}.</div>
              )}
            </div>
            <div className="pt-1">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span>Budget used</span>
                <span className={fin.over_budget ? "text-red-600 font-bold" : "text-slate-600 font-medium"}>{Math.round(fin.budget_used_pct || 0)}% of {fmtFCFA(fin.total_cost)}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full ${fin.over_budget ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, Math.round(fin.budget_used_pct || 0))}%` }} />
              </div>
              {fin.over_budget && (
                <div className="mt-2 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="over-budget-warning">
                  ⚠ Expenditure has exceeded the contract value.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {teamMembers.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-6 p-6" data-testid="project-team-card">
          <h3 className="font-heading font-semibold text-slate-800 mb-4">Team</h3>
          <div className="flex flex-wrap gap-4" data-testid="project-team-list">
            {teamMembers.map((m) => (
              <div key={m.id} data-testid={`team-member-${m.id}`} className="flex items-center gap-3">
                {m.has_photo ? (
                  <img src={`${BACKEND_URL}/api/employees/${m.id}/photo?v=${encodeURIComponent(m.photo_updated_at || "")}`} alt="" className="h-10 w-10 rounded-full object-cover border border-slate-200" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-bold">
                    {(m.name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{m.name}</div>
                  <div className="text-xs text-slate-400 truncate">{m.role || "Member"}{m.profession ? ` · ${m.profession}` : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isRole(ROLES.CEO) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-6 p-6" data-testid="portal-controls">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading font-semibold text-slate-800">Client Portal</h3>
              <p className="text-sm text-slate-500">Share a secure read-only link with the project owner.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">{portal.enabled ? "Enabled" : "Disabled"}</span>
              <Switch checked={portal.enabled} onCheckedChange={togglePortal} data-testid="portal-switch" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Input readOnly value={portal.enabled ? portalUrl : "Portal disabled"} className="flex-1 min-w-[240px] font-mono-num text-xs" data-testid="portal-url" />
            <Button variant="outline" disabled={!portal.enabled} data-testid="portal-copy" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Portal link copied"); }}>
              <Link2 className="h-4 w-4 mr-2" /> Copy
            </Button>
            <Button variant="outline" data-testid="portal-regenerate" onClick={regenPortal}>
              <RefreshCw className="h-4 w-4 mr-2" /> Regenerate
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Link expires on</span>
            <Input type="date" value={portal.expires ? portal.expires.slice(0, 10) : ""} onChange={(e) => setPortalExpiry(e.target.value)} className="h-9 w-[160px]" data-testid="portal-expiry" />
            <Button variant="outline" size="sm" data-testid="portal-expiry-7" onClick={() => presetExpiry(7)}>7 days</Button>
            <Button variant="outline" size="sm" data-testid="portal-expiry-30" onClick={() => presetExpiry(30)}>30 days</Button>
            {portal.expires && <Button variant="ghost" size="sm" data-testid="portal-expiry-clear" onClick={() => setPortalExpiry("")}>Clear</Button>}
            {portalExpired ? <span className="text-xs font-semibold text-red-600" data-testid="portal-expired">Expired — clients can't open this link</span>
              : portal.expires ? <span className="text-xs text-slate-400" data-testid="portal-expiry-note">Link auto-disables after this date</span>
                : <span className="text-xs text-slate-400">No expiry — link stays active until disabled</span>}
          </div>
          <div className="mt-2 text-xs text-slate-400" data-testid="portal-access-log">
            {p?.portal_last_viewed_at
              ? <>Last opened by client: <span className="font-medium text-slate-600">{new Date(p.portal_last_viewed_at).toLocaleString()}</span>{p.portal_view_count ? ` · ${p.portal_view_count} view${p.portal_view_count === 1 ? "" : "s"}` : ""}</>
              : "Not opened by the client yet"}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-6 p-6" data-testid="milestones-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-semibold text-slate-800">Project Timeline</h3>
            <p className="text-sm text-slate-500">Milestones shown to the client on their portal.</p>
          </div>
          {canManageMs && (
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="milestone-template-menu"><LayoutTemplate className="h-4 w-4 mr-1" /> Templates</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {templates.length === 0 && <DropdownMenuItem disabled>No templates yet</DropdownMenuItem>}
                  {[...templates].sort((a, b) => ((b.category === p.category) - (a.category === p.category))).map((t) => (
                    <DropdownMenuItem key={t.id} data-testid={`template-apply-${t.id}`} onClick={() => applyTemplate(t)}>
                      {t.name}
                      {t.category === p.category && t.category ? <span className="ml-2 text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">Suggested</span> : null}
                      <span className="ml-auto text-xs text-slate-400">{t.stages.length}</span>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="template-save-open" onClick={() => setTplOpen(true)} className="text-blue-700">Save current as template…</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={addMs} data-testid="milestone-add"><Plus className="h-4 w-4 mr-1" /> Add stage</Button>
              <Button size="sm" onClick={saveMs} disabled={savingMs} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid="milestone-save">Save timeline</Button>
            </div>
          )}
        </div>

        {milestones.length > 0 && <MilestoneProgress milestones={milestones} />}

        {canManageMs && (
          <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 mb-4 space-y-2" data-testid="ms-email-row">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-slate-600">
                Email the client when a stage is marked <b>Done</b>
                {!p.client_email && <span className="text-amber-600"> · add a client email on the project first</span>}
              </div>
              <Switch checked={msEmails} onCheckedChange={toggleMsEmails} data-testid="ms-email-switch" />
            </div>
            <div className="flex items-center gap-2">
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder={user?.email || "you@example.com"} className="flex-1 min-w-[180px] text-sm" data-testid="ms-test-email" />
              <Button variant="outline" size="sm" onClick={sendTestReminder} data-testid="ms-email-test">Send test</Button>
            </div>
          </div>
        )}

        {canManageMs ? (
          milestones.length === 0 ? <p className="text-sm text-slate-400">No stages yet. Add the first stage to build the client timeline.</p> : (
            <div className="space-y-2">
              {milestones.map((m, idx) => (
                <div key={m.id || idx} onDragOver={(e) => e.preventDefault()} onDrop={() => dropMs(idx)}
                  className={`flex flex-wrap items-center gap-2 border border-slate-100 rounded-lg p-2 ${dragIdx === idx ? "opacity-40" : ""}`} data-testid={`milestone-edit-${idx}`}>
                  <span draggable onDragStart={() => setDragIdx(idx)} onDragEnd={() => setDragIdx(null)} className="cursor-grab active:cursor-grabbing shrink-0" data-testid={`milestone-drag-${idx}`}>
                    <GripVertical className="h-4 w-4 text-slate-400" />
                  </span>
                  <Input value={m.title} onChange={(e) => updateMs(idx, { title: e.target.value })} placeholder="Stage name (e.g. Site survey)" className="flex-1 min-w-[180px]" data-testid={`milestone-title-${idx}`} />
                  <Select value={m.status} onValueChange={(v) => updateMs(idx, { status: v })}>
                    <SelectTrigger className="w-[150px]" data-testid={`milestone-status-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={(m.date || "").slice(0, 10)} onChange={(e) => updateMs(idx, { date: e.target.value })} className="w-[160px]" data-testid={`milestone-date-${idx}`} />
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => removeMs(idx)} data-testid={`milestone-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )
        ) : (
          <MilestoneTimeline milestones={milestones} />
        )}
      </div>

      {canManageMs && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-6 p-6" data-testid="assign-team-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-semibold text-slate-800">Assign Team</h3>
              <p className="text-sm text-slate-500">Employees assigned here see this project under “My Projects”.</p>
            </div>
            <Button size="sm" onClick={saveTeam} disabled={savingTeam} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid="assign-team-save">Save team</Button>
          </div>
          {allEmployees.length === 0 ? <p className="text-sm text-slate-400">No employees available.</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="assign-team-list">
              {allEmployees.map((e) => (
                <label key={e.id} data-testid={`team-emp-${e.id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={team.includes(e.id)} onChange={() => toggleTeam(e.id)} className="h-4 w-4 accent-[#1E3A8A]" />
                  <span className="text-slate-700 truncate flex-1">{e.name}{e.profession ? ` · ${e.profession}` : ""}</span>
                  {team.includes(e.id) && (
                    <select value={teamRoles[e.id] || "Member"} onChange={(ev) => setRole(e.id, ev.target.value)} data-testid={`team-role-${e.id}`} className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white text-slate-600">
                      <option>Lead</option><option>Support</option><option>Member</option>
                    </select>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-6 p-2">
        <Tabs defaultValue="income">
          <TabsList className="m-2">
            <TabsTrigger value="income" data-testid="tab-income">Income ({p.income?.length || 0})</TabsTrigger>
            <TabsTrigger value="expenditure" data-testid="tab-expenditure">Expenditure ({p.expenditures?.length || 0})</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments ({p.payments?.length || 0})</TabsTrigger>
          </TabsList>
          <TabsContent value="income">
            <TxTable rows={p.income} cols={[["date", "Date", (r) => fmtDate(r.date)], ["reference", "Reference", (r) => r.reference || "—"], ["created_by_name", "Recorded By", (r) => r.created_by_name]]} />
          </TabsContent>
          <TabsContent value="expenditure">
            <TxTable rows={p.expenditures} cols={[["date", "Date", (r) => fmtDate(r.date)], ["purpose", "Purpose", (r) => r.purpose], ["created_by_name", "Recorded By", (r) => r.created_by_name]]} />
          </TabsContent>
          <TabsContent value="payments">
            <TxTable rows={p.payments} cols={[["date", "Date", (r) => fmtDate(r.date)], ["purpose", "Purpose", (r) => r.purpose], ["employee_name", "Employee", (r) => r.employee_name || "—"]]} />
          </TabsContent>
        </Tabs>
      </div>

      {canSolar && projectSizings.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-6" data-testid="project-solar-sizings">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 font-semibold text-slate-700"><Sun className="h-4 w-4 text-amber-500" /> Solar sizings</div>
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Array</TableHead><TableHead>Battery</TableHead><TableHead>Inverter</TableHead><TableHead className="text-right">PDF</TableHead></TableRow></TableHeader>
            <TableBody>
              {projectSizings.map((s) => { const r = s.results || {}; return (
                <TableRow key={s.id} data-testid="project-solar-row">
                  <TableCell className="font-medium text-slate-800">{s.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.num_panels || 0} × {s.panel_watt}W</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.num_batteries || 0} batteries</TableCell>
                  <TableCell className="text-sm text-slate-600">{Math.round(r.inverter_w || 0).toLocaleString()} W</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => downloadExport(`/export/solar-sizing/${s.id}.pdf`, `solar_${s.name}.pdf`)} data-testid={`project-solar-pdf-${s.id}`}><FileText className="h-4 w-4" /></Button></TableCell>
                </TableRow>); })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Save timeline as template</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm">Template name</Label>
            <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Standard Solar Install" data-testid="template-name-input" />
            <p className="text-xs text-slate-400">Saves the current {milestones.filter((m) => (m.title || "").trim()).length} stage name(s) for reuse on other projects.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplate} data-testid="template-save-btn" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`font-medium text-slate-800 ${mono ? "font-mono-num" : ""}`}>{value || "—"}</div>
    </div>
  );
}
function FinRow({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm text-slate-500"><Icon className="h-4 w-4" /> {label}</span>
      <span className={`font-mono-num font-semibold ${color}`}>{value}</span>
    </div>
  );
}
function MilestoneProgress({ milestones }) {
  const total = milestones.length;
  const done = milestones.filter((m) => m.status === "done").length;
  const inProg = milestones.filter((m) => m.status === "in_progress").length;
  const pct = total ? Math.round(((done + inProg * 0.5) / total) * 100) : 0;
  return (
    <div className="mb-4" data-testid="milestone-progress">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
        <span>Overall progress</span>
        <span className="font-semibold text-slate-700" data-testid="milestone-progress-pct">{pct}% · {done}/{total} done</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MilestoneTimeline({ milestones }) {
  if (!milestones || milestones.length === 0) return <p className="text-sm text-slate-400" data-testid="milestone-timeline-empty">No timeline stages have been added yet.</p>;
  return (
    <ol className="relative border-l-2 border-slate-100 ml-2" data-testid="milestone-timeline">
      {milestones.map((m, idx) => {
        const s = MS_STATUS[m.status] || MS_STATUS.pending;
        return (
          <li key={m.id || idx} className="ml-5 pb-5 last:pb-0" data-testid={`milestone-item-${idx}`}>
            <span className={`absolute -left-[7px] h-3 w-3 rounded-full ring-4 ring-white ${s.dot}`} />
            <div className="flex items-center gap-2">
              <s.icon className={`h-4 w-4 ${s.text}`} />
              <span className="font-medium text-slate-800">{m.title}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-xs">
              <span className={`font-semibold ${s.text}`}>{s.label}</span>
              {m.date && <span className="text-slate-400">· {fmtDate(m.date)}</span>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function TxTable({ rows, cols }) {  if (!rows || rows.length === 0) return <EmptyState text="No records." />;
  return (
    <Table>
      <TableHeader>
        <TableRow>{cols.map((c) => <TableHead key={c[0]}>{c[1]}</TableHead>)}<TableHead className="text-right">Amount</TableHead></TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            {cols.map((c) => <TableCell key={c[0]} className="text-slate-600">{c[2](r)}</TableCell>)}
            <TableCell className="text-right font-mono-num font-medium">{fmtFCFA(r.amount)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
